import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { env } from '@/lib/env';
import { useContext } from '@/hooks/useContext';
import { logger } from '@/lib/logger';
import { CurrentUserDocument } from '@/features/graphql/generated/graphql';
import { persistentStorage } from '@/lib/storage';
import { apolloClient } from '@/lib/apollo';
import {
  exchangeCodeAsync,
  makeRedirectUri,
  Prompt,
  useAuthRequest,
  useAutoDiscovery,
} from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import { useLazyQuery, useReactiveVar } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { authVar } from '@/lib/apollo/auth';
import { AuthTokens } from '@/lib/types/auth';
import { makeVar } from '@apollo/client';
import { refreshToken } from '@/lib/auth/refresh-token';
import { Platform } from 'react-native';

if (['ios', 'web'].includes(Platform.OS)) {
  try {
    WebBrowser.maybeCompleteAuthSession();
  } catch (error) {
    logger.error('[Auth] maybeCompleteAuthSession failed:', error);
  }
}

// Persist loading state across AuthProvider remounts
const isLoadingAuthVar = makeVar(true);

export interface AuthContext {
  handleLogin: () => void;
  handleLogout: () => void;
  handleRefresh: () => Promise<void>;
  auth: { accessToken: string } | null;
  isLoadingAuth: boolean;
  isAuthenticating: boolean;
}

const ctx = createContext<AuthContext | undefined>(undefined);
ctx.displayName = 'Auth';

export interface AuthProviderProps {
  children?: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const discovery = useAutoDiscovery(
    env.zitadelAuthorityUrl.toString().replace(/\/$/, ''),
  );
  const redirectUri = makeRedirectUri({ scheme: 'fytness' });
  const [request, , promptAsync] = useAuthRequest(
    {
      clientId: env.zitadelClientId,
      redirectUri,
      scopes: [
        'openid',
        'profile',
        'email',
        'offline_access',
        'urn:zitadel:iam:org:project:id:zitadel:aud',
        `urn:zitadel:iam:org:id:${env.zitadelOrganizationId}`,
      ],
      usePKCE: true,
      prompt: Prompt.Login, // Force user to re-authenticate, disable SSO
    },
    discovery,
  );
  const isLoadingRequest = !request;
  const isLoadingAuth = useReactiveVar(isLoadingAuthVar);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handleRefresh = async (): Promise<void> => {
    const currentAuth = authVar();
    if (!currentAuth?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      await refreshToken(currentAuth.refreshToken);
    } catch (error) {
      logger.error('Token refresh failed:', error);
      // If refresh fails, log out the user
      handleLogout();
      throw error;
    }
  };

  useEffect(() => {
    persistentStorage
      .get('auth')
      .then((auth) => {
        authVar(auth ?? null);
        if (!auth) {
          return;
        }

        const expiresIn = (auth.expiresIn + auth.issuedAt) * 1000 - Date.now();
        logger.debug(
          `Token expires in ${Math.round(expiresIn / 1000 / 60)} minutes`,
        );

        // If token is already expired or expires very soon, try to refresh
        if (expiresIn <= 60 * 1000) {
          // Less than 1 minute left
          if (auth.refreshToken) {
            logger.debug('Token expires soon, attempting refresh');
            handleRefresh().catch(() => {
              logger.warn('Token refresh failed on startup, clearing auth');
              authVar(null);
              void persistentStorage.set('auth', null);
            });
          } else {
            logger.warn(
              'Token expired and no refresh token available, clearing auth',
            );
            authVar(null);
            void persistentStorage.set('auth', null);
          }
        }
      })
      .catch(logger.error)
      .finally(() => {
        isLoadingAuthVar(false);
      });
    // Restore the persisted session exactly once on mount. This effect reads
    // `authVar` fresh and does not use `discovery`; depending on `[discovery]` made
    // it re-run when `useAutoDiscovery` resolved async, firing a second startup
    // refresh against the already-rotated one-time-use token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const router = useRouter();
  const handleLogout: AuthContext['handleLogout'] = () => {
    authVar(null);
    void persistentStorage.set('auth', null);
    // Clear Apollo cache to prevent stale user data
    void apolloClient.clearStore();
    router.replace('/(auth)');
  };
  const [fetchUser] = useLazyQuery(CurrentUserDocument);
  const handleLogin: AuthContext['handleLogin'] = () => {
    logger.debug('[Auth] handleLogin called, platform:', Platform.OS);
    setIsAuthenticating(true);
    // Clear any existing auth state and cache to prevent using stale tokens/data
    authVar(null);
    void apolloClient.clearStore();

    // Android: Use manual OAuth flow with custom redirect to app route
    if (Platform.OS === 'android') {
      logger.debug('[Auth] Using Android manual OAuth flow');
      const authUrl = new URL(
        env.zitadelAuthorityUrl.toString().replace(/\/$/, ''),
      );
      authUrl.pathname = '/oauth/v2/authorize';
      authUrl.searchParams.set('client_id', env.zitadelClientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('redirect_uri', 'fytness://auth/callback');
      authUrl.searchParams.set(
        'scope',
        [
          'openid',
          'profile',
          'email',
          'offline_access',
          'urn:zitadel:iam:org:project:id:zitadel:aud',
          `urn:zitadel:iam:org:id:${env.zitadelOrganizationId}`,
        ].join(' '),
      );
      authUrl.searchParams.set('prompt', 'login');

      logger.debug('[Auth] Opening auth URL:', authUrl.toString());

      // Open the browser - the callback route will handle the response
      WebBrowser.openBrowserAsync(authUrl.toString())
        .then((result) => {
          logger.debug('[Auth] Browser result:', result);
        })
        .catch((e) => {
          logger.error('[Auth] Browser error:', e);
        })
        .finally(() => {
          setIsAuthenticating(false);
        });
      return;
    }

    // iOS: Use expo-auth-session's promptAsync
    logger.debug('[Auth] Using iOS expo-auth-session flow');
    void promptAsync()
      .then(async (codeResponse) => {
        // Handle cancellation or non-success responses early
        if (!codeResponse || codeResponse.type !== 'success') {
          logger.error('[Auth] Authentication failed:', {
            type: codeResponse?.type,
            error:
              codeResponse?.type === 'error' ? codeResponse.error : undefined,
            url: codeResponse?.type === 'error' ? codeResponse.url : undefined,
          });
          return null; // Explicitly return null to break the chain
        }

        if (request && discovery) {
          return exchangeCodeAsync(
            {
              clientId: env.zitadelClientId,
              code: codeResponse.params.code,
              extraParams: request.codeVerifier
                ? { code_verifier: request.codeVerifier }
                : undefined,
              redirectUri,
            },
            discovery,
          );
        }
        return null;
      })
      .then(async (tokens) => {
        if (!tokens) {
          logger.debug('No tokens received, authentication flow stopped');
          return;
        }
        const authTokens = tokens as AuthTokens;
        await persistentStorage.set('auth', authTokens);
        authVar(authTokens);
        return tokens;
      })
      .then(async (tokens) => {
        if (!tokens) return;
        await fetchUser();
        // Navigate to private area after successful login
        router.replace('/(private)');
        return tokens;
      })
      .catch((e) => {
        logger.error('Authentication error:', e);
        // Don't navigate away on error, just log it
        // The user is likely still on the login page
      })
      .finally(() => {
        setIsAuthenticating(false);
      });
  };
  const authTokens = useReactiveVar(authVar);
  const value: AuthContext = useMemo(
    () => ({
      handleLogin,
      handleLogout,
      handleRefresh,
      auth: authTokens,
      isLoadingAuth: isLoadingAuth || isLoadingRequest,
      isAuthenticating,
    }),
    [
      handleLogin,
      handleLogout,
      handleRefresh,
      authTokens,
      isLoadingAuth,
      isLoadingRequest,
      isAuthenticating,
    ],
  );
  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useAuth = () => useContext(ctx);
